import type { ZodType } from "zod";
import { KevApiError, codeForStatus } from "./errors";
import { apiErrorSchema } from "./schemas";

/**
 * The one HTTP client. There is deliberately no second implementation.
 *
 * Mocks live at the NETWORK boundary (MSW intercepts fetch), not behind a
 * swappable interface. That's the whole seam design: this file runs identically
 * in dev against MSW, in tests against setupServer, and in production against
 * the real API. Swapping is `VITE_MOCKS=off` — no app-code change.
 *
 * A swappable ApiClient interface would let mocks return happy-path objects and
 * never exercise status codes, token attachment, latency or the 402/429/503
 * paths — which are the entire cost story. So: don't add one.
 *
 * The base URL is RELATIVE (`/api`) in dev and prod alike. In prod that means
 * CloudFront routes /api/* to the API — same-origin, so no CORS, and it's exactly
 * what MSW intercepts in dev. Never put an execute-api hostname here; a test
 * enforces that (src/test/no-aws.test.ts).
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

/**
 * Set once at boot by the auth layer. The client pulls the token rather than
 * callers pushing it — a caller that forgets is a bug you find in production.
 *
 * ASYNC on purpose, even though the mock resolves instantly: real Cognito needs
 * a silent refresh when the token is near expiry. Making this sync in pass 1
 * would mean rewriting every call site in pass 2.
 */
let tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(fn: (() => Promise<string | null>) | null) {
  tokenProvider = fn;
}

/** Called on 401 so the auth layer can drop the session exactly once. */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(fn: (() => void) | null) {
  onUnauthenticated = fn;
}

/**
 * Fire the 401 handler from OUTSIDE this module.
 *
 * `request` and `requestVoid` call it themselves, but chat does not go through
 * either: `chat.ts` imports `rawFetch` directly, because the streaming reply must
 * not inherit the 15s timeout the other calls get. It therefore builds its own
 * errors — and never dropped the session, so a chat 401 left the app looking
 * signed in with a dead chat box and no explanation. `useChat` even documents the
 * opposite ("the client's 401 handler already dropped the session"), which is how
 * it went unnoticed: the comment described the behaviour of the path chat does
 * not take.
 */
export function notifyUnauthenticated() {
  onUnauthenticated?.();
}

interface RequestOptions {
  /* PATCH joined the list for /fantasy/teams, which is the first partial
     update in the product: a squad rename changes one field and must not
     restate the roster, because the server refuses a body carrying one. */
  /* PUT joined for /fantasy/league/{sport}, which is the first WHOLE-OBJECT
     replace in the product: a saved league is one thing a person edits as one
     thing — the shape and the names together — so a partial update would let a
     squad be stored against a formation the client never sent. */
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the Authorization header (public endpoints like /waitlist). */
  anonymous?: boolean;
}

/**
 * How long any single API call gets before we treat it as failed.
 *
 * `fetch` HAS NO DEFAULT TIMEOUT, and until now neither did this client — the
 * only `signal` here was one a caller happened to pass, and the callers that
 * matter most pass none. A request that HANGS, as distinct from one that fails,
 * therefore never settled: the React Query watching it stayed `isLoading`
 * forever, and RequireOnboarded — which gates every route under /app on exactly
 * that flag, with no claim to fall back on — rendered LOADING… for good.
 *
 * That is precisely the shape of the bug this was written for: /pricing carries
 * no guard and rendered fine, while the models and the board sat on a spinner,
 * for an account whose token was perfectly valid.
 *
 * Fifteen seconds. Long enough that a cold Lambda on a bad connection still
 * lands, short enough that a guard which fails open does so while someone is
 * still looking at the screen. A timed-out call becomes a REJECTION, which every
 * consumer already handles — the guards fail open, the queries surface an error.
 * Any of those is recoverable. A spinner is not.
 */
const API_TIMEOUT_MS = 15_000;

/**
 * The caller's signal and ours, as one. `AbortSignal.any` would do this in a
 * line but is too new to rely on across the browsers this ships to, so it is
 * wired by hand — and `done()` must always run, or a long-lived caller signal
 * accumulates a listener per request.
 */
function boundedSignal(outer?: AbortSignal) {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), API_TIMEOUT_MS);
  const relay = () => ctl.abort();
  if (outer?.aborted) ctl.abort();
  else outer?.addEventListener("abort", relay);
  return {
    signal: ctl.signal,
    done: () => {
      clearTimeout(timer);
      outer?.removeEventListener("abort", relay);
    },
  };
}

/**
 * DELIBERATELY UNBOUNDED, and it must stay that way.
 *
 * `chat.ts` calls this directly for POST /chat, which can honestly take half a
 * minute — a model composing a real answer. The timeout below belongs to the
 * JSON helpers, not here.
 *
 * An earlier version of the timeout sat in this function, which silently applied
 * it to chat as well and would have cut replies off at fifteen seconds. The
 * comment on `endpoints.ts`'s `me` had warned about exactly that; putting the
 * bound one layer too low ignored it.
 */
async function rawFetch(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";

  if (!opts.anonymous && tokenProvider) {
    const token = await tokenProvider();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  return fetch(`${BASE}${path}`, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });
}

/**
 * rawFetch, bounded — and the bound stays up until the BODY has been read.
 *
 * ⚠️ THE SUBTLETY THAT MADE THE FIRST VERSION OF THIS WRONG. `fetch` resolves as
 * soon as the response HEADERS arrive; the body streams afterwards. This used to
 * return the Response and clear the timeout in a `finally` at that moment, so
 * every `res.json()` in this file ran with no bound at all. A server that sent
 * headers and then stalled its body — a half-open connection, a proxy that gives
 * up mid-stream — left the parse pending forever.
 *
 * Which is exactly the failure the timeout above was written for, arriving one
 * step later than the fix reached: the React Query watching it stays
 * `isLoading`, and `RequireOnboarded` gates every route under /app on that flag
 * with no claim to fall back on, so the app sits on LOADING… for good. The
 * docstring on API_TIMEOUT_MS describes that exact shape.
 *
 * So the caller does its parsing INSIDE the bound, via readBody. Aborting the controller
 * cancels the body stream too, which turns the hang into a rejection — and every
 * consumer already handles a rejection: the guards fail open, the queries
 * surface an error. Any of those is recoverable; a spinner is not.
 */
async function withBound<T>(
  path: string,
  opts: RequestOptions,
  readBody: (res: Response) => Promise<T>,
): Promise<T> {
  const bound = boundedSignal(opts.signal);
  try {
    const res = await rawFetch(path, { ...opts, signal: bound.signal });
    return await readBody(res);
  } finally {
    bound.done();
  }
}

async function toError(res: Response): Promise<KevApiError> {
  let payload: unknown = {};
  try {
    payload = await res.json();
  } catch {
    /* a non-JSON error body is still an error; fall through to defaults */
  }

  const parsed = apiErrorSchema.safeParse(payload);
  const body = parsed.success ? parsed.data : { error: "unknown" };
  const code = codeForStatus(res.status, body.error);

  /* Prefer the header — it's the HTTP-native signal and survives proxies that
     rewrite bodies. Fall back to the body for mocks/backends that omit it. */
  const header = res.headers.get("retry-after");
  const retryAfterSec = header ? Number(header) : body.retryAfterSec;

  return new KevApiError(code, res.status, body.message ?? body.error, {
    ...body,
    retryAfterSec: Number.isFinite(retryAfterSec) ? retryAfterSec : undefined,
  });
}

/**
 * Typed request. The response is schema-parsed at this boundary — a backend that
 * drifts fails here, loudly, rather than three components downstream.
 */
export async function request<T>(
  path: string,
  schema: ZodType<T>,
  opts: RequestOptions = {},
): Promise<T> {
  return withBound(path, opts, async (res) => {
    if (!res.ok) {
      /* Inside the bound too: toError reads the error body, which can stall in
         exactly the same way a success body can. */
      const err = await toError(res);
      if (err.code === "unauthenticated") onUnauthenticated?.();
      throw err;
    }

    const json = await res.json();
    const parsed = schema.safeParse(json);
    if (!parsed.success) {
      /* Treat a contract violation as a real failure. Silently coercing is how
         you end up rendering `undefined` to a paying user. */
      throw new KevApiError(
        "unknown",
        res.status,
        `Response did not match the contract for ${path}: ${parsed.error.message}`,
      );
    }
    return parsed.data;
  });
}

/**
 * Same request, no body.
 *
 * `request<T>` unconditionally calls `res.json()`, which throws on a 204 (a
 * DELETE has no body to parse, and there's no schema to parse it against
 * anyway). Rather than make every schema optional to accommodate one verb,
 * this is a sibling that shares the same fetch/error/401 path and simply
 * stops before the parse step.
 */
export async function requestVoid(path: string, opts: RequestOptions = {}): Promise<void> {
  return withBound(path, opts, async (res) => {
    if (!res.ok) {
      const err = await toError(res);
      if (err.code === "unauthenticated") onUnauthenticated?.();
      throw err;
    }
  });
}

/** Escape hatch for endpoints that stream rather than return JSON (see chat.ts). */
export { rawFetch };
